/**
 * Pure view model for the mission status-details panel: turns the EI run,
 * its process status (lease / trigger / scheduler) and the originating
 * Engineering Request into plain-language facts an operator can act on —
 * "is anything working on this right now, and if not, why".
 */
import type { StatusTone } from '../design-system/foundation';
import type { EiInvestigationRun } from '../types/engineeringIntelligence';
import type { EngineeringMissionStatus } from '../services/missionStatus';
import type { EngineeringRequest } from '../services/engineeringRequests';
import { eiStateLabel } from './eiLifecycle';
import { describeEiToolInvocation } from './eiToolActivity';
import { EI_DONE_STATES, EI_NEEDS_YOU_STATES, reconcileTimelineWithState } from './missionHeaderStages';
import { ensoUtcTestedStep } from './ensoUtc';

export interface MissionStatusHeadline {
  label: string;
  tone: StatusTone;
  pulse?: boolean;
  detail: string;
}

export interface MissionStatusFact {
  label: string;
  value: string;
  hint?: string;
  tone?: StatusTone;
}

export interface MissionStatusEvent {
  at?: string;
  title: string;
  detail?: string;
  tone: StatusTone;
  /** How many identical consecutive entries this one stands for. */
  count?: number;
}

export interface MissionStatusStage {
  id: string;
  label: string;
  status: string;
  at?: string;
  detail?: string;
}

export interface MissionStatusSummary {
  headline: MissionStatusHeadline;
  facts: MissionStatusFact[];
  /** What is wrong right now — empty when the run is healthy or finished. */
  problems: MissionStatusEvent[];
  /** Earlier problems and the run's progress notes, newest first. */
  history: MissionStatusEvent[];
  activity: MissionStatusEvent[];
  /** Set when nothing has happened for a while, so old activity is not read as live. */
  activityNote?: string;
  stages: MissionStatusStage[];
}

export interface MissionStatusInput {
  run?: EiInvestigationRun | null;
  status?: EngineeringMissionStatus | null;
  request?: EngineeringRequest | null;
  now?: number;
}

const DONE_STATES = EI_DONE_STATES;
const NEEDS_YOU_STATES = EI_NEEDS_YOU_STATES;

/**
 * run.blocked_reasons is really the run's notes log: most entries ("seal",
 * "evidence_exhaustion", "operator_guidance", …) record normal progress, and
 * rendering them all as red "Blocked" rows buried the few real problems.
 * Entries are "<code>: <detail>"; codes listed here are progress notes.
 */
const NOTE_CODES = new Set([
  'seal', 'seal_deferred', 'evidence_exhaustion', 'hypothesis_elimination', 'operator_guidance',
  'hypotheses_refreshed_from_human_answer', 'autonomous_resolution', 'investigate_grounding',
  'restart_workspace_reset', 'superseded_by_restart', 'investigation_converged', 'engineering_default',
  'transport_swap_already_satisfied', 'transport_already_satisfied_advance', 'execution_adapted',
  'notify_inventory_advance', 'himitsu_pass2', 'remediation_critique_blast_radius',
  'deterministic_patch', 'deterministic_notification_patch',
]);

/** Plain-language names for the problem codes operators actually see. */
const PROBLEM_TITLES: Record<string, string> = {
  human_escalation: 'Escalated to you',
  provider: 'Model provider failed',
  infrastructure_failure: 'Infrastructure failure',
  closure_blocked: 'Closure blocked',
  reflect: 'Reflection step failed',
  generate_patch: 'Patch generation failed',
  generate_plan: 'Plan generation failed',
  plan_generation_failed: 'No implementation plan could be generated',
  generate_rollback: 'Rollback generation failed',
};

const FAILURE_WORDS = /\b(fail(ed|ure|s)?|error|cannot|unable|invalid|timed? ?out|refused|not found|exceeded|did not pass)\b/i;

export interface ClassifiedReason {
  kind: 'problem' | 'note';
  title: string;
  detail?: string;
}

/** Splits one blocked_reasons entry into a titled problem or progress note. */
export function classifyRunReason(reason: string): ClassifiedReason {
  const text = reason.trim();
  const m = /^([a-z][a-z0-9_]*)\s*:\s*([\s\S]*)$/.exec(text);
  const code = m?.[1] ?? '';
  const rest = m ? m[2].trim() : text;
  if (code && NOTE_CODES.has(code)) {
    return { kind: 'note', title: humanizeToken(code), detail: truncate(rest) };
  }
  if (code && (PROBLEM_TITLES[code] || code.startsWith('improve_plan'))) {
    return { kind: 'problem', title: PROBLEM_TITLES[code] ?? `${humanizeToken(code)} failed`, detail: truncate(rest) };
  }
  if (FAILURE_WORDS.test(text)) {
    return { kind: 'problem', title: code ? humanizeToken(code) : 'Blocked', detail: truncate(rest) };
  }
  return { kind: 'note', title: code ? humanizeToken(code) : 'Note', detail: truncate(rest) };
}

/** The run's latest recorded problem, formatted for a sentence. */
function latestProblemReason(run: EiInvestigationRun | null | undefined): ClassifiedReason | null {
  const reasons = run?.blocked_reasons ?? [];
  for (let i = reasons.length - 1; i >= 0; i -= 1) {
    const c = classifyRunReason(reasons[i]);
    if (c.kind === 'problem') return c;
  }
  return null;
}

function reasonSentence(c: ClassifiedReason): string {
  return c.detail ? `${c.title}: ${c.detail}` : c.title;
}

export function humanizeToken(value?: string | null): string {
  const text = String(value ?? '').replace(/[_.-]+/g, ' ').trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

/** 45s · 12m · 3h 5m · 2d 4h */
export function formatSpan(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
}

/** Go zero times ("0001-01-01T00:00:00Z") mean "never" — treat as missing. */
export function parseTimestamp(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t) || t < Date.UTC(2000, 0, 1)) return null;
  return t;
}

function secondsSince(iso: string | undefined | null, now: number): number | null {
  const t = parseTimestamp(iso);
  return t == null ? null : Math.max(0, (now - t) / 1000);
}

function secondsUntil(iso: string | undefined | null, now: number): number | null {
  const t = parseTimestamp(iso);
  return t == null ? null : Math.max(0, (t - now) / 1000);
}

function stateChangeTitle(from?: string, to?: string): string {
  const toLabel = eiStateLabel(to);
  if (!from) return toLabel;
  const fromLabel = eiStateLabel(from);
  // Several FSM states share one operator label — fall back to the raw step
  // names so "Investigation → Investigation" never hides what changed.
  if (fromLabel === toLabel) return `${humanizeToken(from)} → ${humanizeToken(to)}`;
  return `${fromLabel} → ${toLabel}`;
}

/** "just now" · "14m ago" · "—" for missing or zero times. */
export function agoLabel(iso: string | undefined | null, now: number): string {
  const s = secondsSince(iso, now);
  if (s == null) return '—';
  if (s < 45) return 'just now';
  return `${formatSpan(s)} ago`;
}

function truncate(text: string | undefined, max = 220): string | undefined {
  if (!text) return undefined;
  const t = text.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function buildHeadline(input: MissionStatusInput, now: number): MissionStatusHeadline {
  const { run, status, request } = input;
  const trigger = status?.trigger;
  const state = String(run?.state ?? '').toUpperCase();

  if (!run) {
    if (status && !status.ei_enabled) {
      return { label: 'Engine disabled', tone: 'error', detail: 'Engineering Intelligence is turned off, so no mission run can start.' };
    }
    if (trigger?.status === 'blocked') {
      return {
        label: 'Could not start',
        tone: 'error',
        detail: `Auto-start gave up after ${trigger.attempt_count} attempt${trigger.attempt_count === 1 ? '' : 's'}${trigger.last_error ? `: ${truncate(trigger.last_error, 160)}` : '.'}`,
      };
    }
    if (trigger?.status === 'failed') {
      const wait = secondsUntil(trigger.next_attempt_at, now);
      return {
        label: 'Start failed — retrying',
        tone: 'warning',
        detail: `Attempt ${trigger.attempt_count}/${trigger.max_attempts} failed${wait != null ? `; next try in ${formatSpan(wait)}` : ''}${trigger.last_error ? ` — ${truncate(trigger.last_error, 140)}` : ''}`,
      };
    }
    if (trigger?.status === 'pending' || trigger?.status === 'running') {
      return { label: 'Starting mission', tone: 'info', pulse: true, detail: `Auto-start ${trigger.status} since ${agoLabel(trigger.updated_at, now)}.` };
    }
    if (request?.status === 'clarification_required') {
      return { label: 'Needs you', tone: 'warning', detail: 'The request is waiting for an answer to a clarification question.' };
    }
    return {
      label: 'No mission run yet',
      tone: 'neutral',
      detail: request ? `Request is ${humanizeToken(request.status).toLowerCase()}; no investigation run has been created.` : 'No investigation run has been created.',
    };
  }

  if (DONE_STATES.has(state)) {
    return { label: 'Complete', tone: 'success', detail: `Finished as ${eiStateLabel(state).toLowerCase()} ${agoLabel(run.updated_at, now)}.` };
  }
  if (NEEDS_YOU_STATES.has(state)) {
    return {
      label: 'Needs you',
      tone: 'warning',
      detail: state === 'NEEDS_HUMAN'
        ? 'The mission paused for a human decision — answer it on the Approval tab.'
        : 'The implementation package is sealed and waiting for approval.',
    };
  }

  const proc = status?.run;
  const idle = proc?.idle_seconds ?? secondsSince(run.updated_at, now) ?? 0;
  const staleAfter = status?.stale_after_sec ?? 600;
  const stale = proc ? proc.stale : idle >= staleAfter;
  if (state === 'EXECUTION_BLOCKED') {
    return { label: 'Execution blocked', tone: 'warning', detail: `A required capability was unavailable; it is retried automatically (${run.execution_blocked_retries ?? 0} so far).` };
  }
  // A recorded problem (e.g. model calls exhausted their retries) is the real
  // reason a run went quiet — say that instead of a generic stall. Progress
  // notes such as evidence_exhaustion are not problems.
  const blocker = latestProblemReason(run);
  if (stale && blocker) {
    return {
      label: 'Blocked',
      tone: 'error',
      detail: `No progress for ${formatSpan(idle)} — ${truncate(reasonSentence(blocker), 180)}`,
    };
  }
  if (stale) {
    const scheduler = status?.scheduler;
    if (proc?.lease_active) {
      return {
        label: `No progress for ${formatSpan(idle)}`,
        tone: 'warning',
        detail: `The ${proc.lease_holder || 'scheduler'} claimed this run ${agoLabel(proc.lease_claimed_at, now)} but nothing has been saved since ${agoLabel(run.updated_at, now)} — it may be waiting on a slow model call, or stuck.`,
      };
    }
    if (scheduler && scheduler.ticks_completed > 0 && !scheduler.running) {
      return { label: 'Stalled', tone: 'error', detail: `No progress for ${formatSpan(idle)} and the resume scheduler is not running.` };
    }
    return { label: 'Stalled', tone: 'warning', detail: `No progress for ${formatSpan(idle)}; the scheduler should resume it on its next sweep.` };
  }
  return {
    label: 'Working',
    tone: 'info',
    pulse: true,
    detail: `Last progress ${agoLabel(run.updated_at, now)}.`,
  };
}

function buildFacts(input: MissionStatusInput, now: number): MissionStatusFact[] {
  const { run, status, request } = input;
  const facts: MissionStatusFact[] = [];
  if (run) {
    facts.push({ label: 'Stage', value: eiStateLabel(run.state), hint: run.state });
    if (run.remediation_phase) facts.push({ label: 'Current step', value: humanizeToken(run.remediation_phase) });
    if (run.max_iterations) {
      const it = run.iteration ?? 0;
      facts.push({
        label: 'Investigation loop',
        value: `${it} / ${run.max_iterations}`,
        tone: it >= run.max_iterations ? 'warning' : undefined,
        hint: it >= run.max_iterations ? 'Iteration budget used up' : undefined,
      });
    }
    if (run.remediation_iteration) {
      facts.push({
        label: 'Patch revision',
        value: run.max_remediation_iterations ? `${run.remediation_iteration} / ${run.max_remediation_iterations}` : String(run.remediation_iteration),
      });
    }
    const elapsed = status?.run?.elapsed_seconds ?? secondsSince(run.created_at, now);
    facts.push({ label: 'Running for', value: formatSpan(elapsed), hint: run.created_at ? `Started ${new Date(run.created_at).toLocaleString()}` : undefined });
    facts.push({
      label: 'Last progress',
      value: agoLabel(run.updated_at, now),
      hint: run.updated_at ? new Date(run.updated_at).toLocaleString() : undefined,
      tone: status?.run?.stale ? 'warning' : undefined,
    });
    const proc = status?.run;
    if (proc) {
      facts.push({
        label: 'Worker',
        value: proc.lease_active ? `${proc.lease_holder || 'claimed'} · ${agoLabel(proc.lease_claimed_at, now)}` : proc.auto_resumable ? 'Not claimed' : 'Not needed',
        hint: proc.lease_active
          ? 'Holds the resume lease for this run'
          : proc.auto_resumable && proc.resume_eligible_at
            ? `Scheduler may resume it in ${formatSpan(secondsUntil(proc.resume_eligible_at, now))}`
            : undefined,
      });
    }
    const model = [run.provider, run.model].filter(Boolean).join(' / ') || run.engine_name;
    if (model) facts.push({ label: 'Model', value: model });
    if (run.execution_mode) facts.push({ label: 'Execution mode', value: humanizeToken(run.execution_mode) });

    const retries: string[] = [];
    if (run.validation_retries) retries.push(`${run.validation_retries} validation`);
    if (run.plan_generation_failures) retries.push(`${run.plan_generation_failures} plan generation`);
    if (run.consistency_retries) retries.push(`${run.consistency_retries} consistency`);
    if (run.execution_blocked_retries) retries.push(`${run.execution_blocked_retries} execution-blocked`);
    if (run.llm_retries?.length) retries.push(`${run.llm_retries.length} model call`);
    if (retries.length) {
      // Lifetime counters: only warn while retries are still happening.
      const lastRetry = Math.max(0, ...(run.llm_retries ?? []).map((r) => parseTimestamp(r.at) ?? 0));
      const recent = lastRetry > 0 && now - lastRetry < 3600_000;
      facts.push({
        label: 'Retries (all time)',
        value: retries.join(' · '),
        tone: recent ? 'warning' : undefined,
        hint: lastRetry ? `Last retry ${agoLabel(new Date(lastRetry).toISOString(), now)}` : undefined,
      });
    }
    const extensions = (run.budget_extensions ?? 0) + (run.gate_escalation_extensions ?? 0);
    if (extensions) facts.push({ label: 'Budget extensions', value: String(extensions) });
  }
  if (request) {
    facts.push({ label: 'Request status', value: humanizeToken(request.status), hint: request.submitted_by ? `Submitted by ${request.submitted_by}` : undefined });
  }
  const trigger = status?.trigger;
  // Once the run exists, a successful auto-start is history, not status.
  if (trigger && !(run && trigger.status === 'succeeded')) {
    facts.push({
      label: 'Auto-start',
      value: `${humanizeToken(trigger.status)}${trigger.attempt_count ? ` · ${trigger.attempt_count}/${trigger.max_attempts} attempts` : ''}`,
      tone: trigger.status === 'blocked' ? 'error' : trigger.status === 'failed' ? 'warning' : undefined,
    });
  }
  const scheduler = status?.scheduler;
  // The resume scheduler is global and only picks up runs that can continue
  // on their own; beside a run waiting on a person or already finished its
  // "Running · ticked 28s ago" reads as live progress that is not happening.
  const runState = String(run?.state ?? '').toUpperCase();
  const schedulerMatters = !run || !(NEEDS_YOU_STATES.has(runState) || DONE_STATES.has(runState));
  if (scheduler && schedulerMatters) {
    facts.push({
      label: 'Resume scheduler',
      value: scheduler.ticks_completed === 0 ? 'Not ticked yet' : scheduler.running ? `Running · ticked ${formatSpan(scheduler.seconds_since_last_tick)} ago` : 'Not running',
      tone: scheduler.ticks_completed > 0 && !scheduler.running ? 'error' : undefined,
    });
  }
  return facts;
}

function byNewest(a: MissionStatusEvent, b: MissionStatusEvent): number {
  return (Date.parse(b.at ?? '') || 0) - (Date.parse(a.at ?? '') || 0);
}

/** When the run entered its current state; problems before it are history. */
function currentStateSince(run: EiInvestigationRun | null | undefined): number | null {
  const last = (run?.transitions ?? []).reduce<number | null>((acc, t) => {
    const at = parseTimestamp(t.at);
    return at != null && (acc == null || at > acc) ? at : acc;
  }, null);
  return last ?? parseTimestamp(run?.created_at);
}

function buildProblems(input: MissionStatusInput, now: number): { problems: MissionStatusEvent[]; history: MissionStatusEvent[] } {
  const { run, status } = input;
  const current: MissionStatusEvent[] = [];
  const history: MissionStatusEvent[] = [];
  const seen = new Set<string>();
  const push = (list: MissionStatusEvent[], e: MissionStatusEvent) => {
    const key = `${e.title}|${e.detail ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    list.push(e);
  };

  const state = String(run?.state ?? '').toUpperCase();
  const finished = DONE_STATES.has(state);
  const since = currentStateSince(run);
  // A timestamped failure is current only if it happened in the run's present
  // state; anything older has already been retried past.
  const isCurrent = (at?: string) => {
    if (finished) return false;
    const t = parseTimestamp(at);
    if (t == null) return false;
    return since != null ? t >= since : now - t < 3600_000;
  };

  // blocked_reasons carry no timestamps. The latest problem is current while
  // the run is held (waiting on a person, blocked, or stalled); the rest are history.
  const held = !finished && (NEEDS_YOU_STATES.has(state) || state === 'EXECUTION_BLOCKED' || Boolean(status?.run?.stale));
  const reasons = (run?.blocked_reasons ?? []).map(classifyRunReason);
  let lastProblemIdx = -1;
  reasons.forEach((c, i) => {
    if (c.kind === 'problem') lastProblemIdx = i;
  });
  reasons.forEach((c, i) => {
    const event: MissionStatusEvent = {
      title: c.title,
      detail: c.detail,
      tone: c.kind === 'note' ? 'neutral' : state === 'NEEDS_HUMAN' && c.title === 'Escalated to you' ? 'warning' : 'error',
    };
    push(held && i === lastProblemIdx ? current : history, event);
  });

  for (const r of run?.llm_retries ?? []) {
    push(isCurrent(r.at) ? current : history, {
      at: r.at,
      title: `Model call retried — ${humanizeToken(r.operation) || 'call'}${r.attempt ? ` (attempt ${r.attempt}${r.max_attempts ? `/${r.max_attempts}` : ''})` : ''}`,
      detail: truncate(r.error),
      tone: 'warning',
    });
  }
  for (const t of (run?.tool_trace ?? []).filter((x) => x.error)) {
    push(isCurrent(t.at) ? current : history, { at: t.at, title: `Tool failed — ${t.call?.tool ?? 'tool'}`, detail: truncate(t.error), tone: 'warning' });
  }
  for (const e of (run?.execution_trace ?? []).filter((x) => x.error)) {
    const at = e.finished_at ?? e.started_at;
    push(isCurrent(at) ? current : history, { at, title: `Execution step failed${e.step_id ? ` — ${e.step_id}` : ''}`, detail: truncate(e.error), tone: 'error' });
  }
  if (run?.consistency_sync?.last_error) {
    push(finished ? history : current, { title: 'Consistency sync degraded', detail: truncate(run.consistency_sync.last_error), tone: 'warning' });
  }
  if (status?.trigger?.last_error && status.trigger.status !== 'succeeded') {
    push(current, { at: status.trigger.updated_at, title: 'Auto-start error', detail: truncate(status.trigger.last_error), tone: 'error' });
  }
  if (status?.scheduler?.last_tick_error) {
    push(current, { at: status.scheduler.last_tick_at, title: 'Scheduler error', detail: truncate(status.scheduler.last_tick_error), tone: 'error' });
  }
  // Untimed entries keep their log order (newest last) → reverse, then timed ones by time.
  const order = (list: MissionStatusEvent[]) => {
    const timed = list.filter((e) => e.at).sort(byNewest);
    const untimed = list.filter((e) => !e.at).reverse();
    return [...untimed, ...timed];
  };
  return { problems: order(current), history: order(history) };
}

function buildActivity(run: EiInvestigationRun | null | undefined): MissionStatusEvent[] {
  if (!run) return [];
  const events: MissionStatusEvent[] = [];
  for (const t of (run.transitions ?? []).slice(-8)) {
    events.push({
      at: t.at,
      title: stateChangeTitle(t.from, t.to),
      detail: truncate(t.reason, 160),
      tone: NEEDS_YOU_STATES.has(String(t.to).toUpperCase()) ? 'warning' : 'neutral',
    });
  }
  for (const call of (run.tool_trace ?? []).slice(-8)) {
    const tool = call.call?.tool ?? 'tool';
    const what = describeEiToolInvocation(call);
    events.push({
      at: call.at,
      title: `${call.error ? 'Tool failed' : 'Ran tool'} · ${tool}${call.duration_ms != null ? ` (${formatSpan(call.duration_ms / 1000)})` : ''}`,
      detail: truncate(
        call.error || what || call.result_summary || call.call?.objective || call.call?.purpose,
        160,
      ),
      tone: call.error ? 'warning' : 'neutral',
    });
  }
  const sorted = events.filter((e) => e.at).sort(byNewest);
  // Collapse identical back-to-back entries (the same probe three times) into one.
  const merged: MissionStatusEvent[] = [];
  for (const e of sorted) {
    const prev = merged[merged.length - 1];
    if (prev && prev.title === e.title && prev.detail === e.detail) {
      prev.count = (prev.count ?? 1) + 1;
    } else {
      merged.push({ ...e });
    }
  }
  return merged.slice(0, 8);
}

function buildActivityNote(input: MissionStatusInput, activity: MissionStatusEvent[], now: number): string | undefined {
  const { run } = input;
  if (!run) return undefined;
  const newest = parseTimestamp(activity[0]?.at ?? run.updated_at);
  if (newest == null || now - newest < 10 * 60_000) return undefined;
  const state = String(run.state ?? '').toUpperCase();
  const quiet = `Nothing has run for ${formatSpan((now - newest) / 1000)}`;
  if (DONE_STATES.has(state)) return `${quiet} — the mission is finished.`;
  if (state === 'NEEDS_HUMAN') return `${quiet} — it is waiting on you.`;
  if (NEEDS_YOU_STATES.has(state)) return `${quiet} — it is waiting for approval.`;
  return `${quiet}.`;
}

export function buildMissionStatusSummary(input: MissionStatusInput): MissionStatusSummary {
  const now = input.now ?? Date.now();
  const { problems, history } = buildProblems(input, now);
  const activity = buildActivity(input.run);
  const blocker = latestProblemReason(input.run);
  const timeline = ensoUtcTestedStep(
    reconcileTimelineWithState(input.run?.himitsu?.timeline ?? [], input.run?.state, blocker ? reasonSentence(blocker) : undefined),
    input.run,
  );
  return {
    headline: buildHeadline(input, now),
    facts: buildFacts(input, now),
    problems,
    history,
    activity,
    activityNote: buildActivityNote(input, activity, now),
    stages: timeline.map((s) => ({
      id: s.id,
      label: s.label,
      status: s.status,
      at: parseTimestamp(s.at) == null ? undefined : s.at,
      detail: s.detail,
    })),
  };
}
